package com.neurobs

import scala.concurrent.{ExecutionContext, Future}
import io.circe.Decoder
import com.neurobs.errors._

import java.net.URLEncoder

package object http {

	object HTTPRequestType extends Enumeration {
		val Get = Value("GET")
		val Post = Value("POST")
		val Delete = Value("DELETE")
		val Put = Value("PUT")
	}

	case class HTTPResponseError(code: Int) extends ErrorReport {
		override def description: String = {
			"The server returned an error: " + (code match {
				case 401 => "Unauthorized call (401)"
				case 403 => "Permission is denied (403)"
				case 404 => "Not found (404)"
				case 500 => "The server had an internal error (500)"
				case c => s"Error code $c"
			})
		}
	}

	val httpStatusOK = 200
	val contentTypeHeader = "Content-Type"

	trait BodyData {
		def asString(): Option[String]
		def asBytes(): Array[Byte]
	}

	trait DataSource
	case class StringDataSource(val s: String) extends DataSource
	case class FileDataSource(val p: java.nio.file.Path) extends DataSource
	object EmptyDataSource extends DataSource

	case class MultiPartPart(key: String, data: DataSource, filename: Option[String] = None)
	case class MultiPartSource(parts: Iterable[MultiPartPart]) extends DataSource

	implicit def fromString(s: String): DataSource = StringDataSource(s)
	implicit def fromPath(p: java.nio.file.Path): DataSource = FileDataSource(p)

	/** Interface to a response in process
	 */
	trait RequestProcess {
		/** Aborts the request if possible */
		def abort(): Unit

		/** Sets handler called on progress updates
		 *
		 * @param onProgress Callback taking the fraction complete
		 */
		def setOnProgress(onProgress: (Double) => Unit): Unit
	}

	type RequestCall[T] = (RequestProcess, Future[Result[T]])

	trait Session {
		def setCommonHeaders(headers: Iterable[(String, String)]): Unit

		def call(url: String, t: HTTPRequestType.Value, body: DataSource = EmptyDataSource, binaryResponseExpected: Boolean = false): RequestCall[BodyData]
	}

	trait URLUtils {
		def encode(s: String): String
	}

	object JVMURLUtils extends URLUtils {
		override def encode(s: String): String = URLEncoder.encode(s, "UTF-8")
	}

	def toObjectResponse[T](callValue: RequestCall[BodyData])(implicit d: Decoder[T], executor: ExecutionContext): RequestCall[T] = {
		(
			callValue._1,
			callValue._2 map {
				case Left(e) => Left(e)
				case Right(body) => body.asString() match {
					case None => Left(StringErrorReport("The server response was not valid UTF-8."))
					case Some(s) => io.circe.parser.decode[T](s) match {
						case Left(_) => Left(StringErrorReport("The server response could not be parsed."))
						case Right(obj) => Right(obj)
					}
				}
			}
		)
	}

	def toNoResponse(callValue: RequestCall[BodyData])(implicit executor: ExecutionContext): RequestCall[Unit] = {
		(
			callValue._1,
			callValue._2.map(_.map(_ => Unit))
		)
	}
}
